import React, { useState, useEffect, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  Box,
  Grid,
  Button,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Check as CheckIcon,
  ArrowForward as ArrowForwardIcon,
  Timer as TimerIcon,
  Verified as VerifiedIcon,
  Update as UpdateIcon,
  Groups as GroupsIcon,
  MoneyOff as FreeIcon,
  CreditCard as CreditCardIcon,
  AccountBalance as BankIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';

import SEOHead from '../components/SEO/SEOHead';
import PageHeader from '../components/Layout/PageHeader';
import NewsletterSection from '../components/Layout/NewsletterSection';
import TrustStrip from '../components/Layout/TrustStrip';
import { useLocalizedPath } from '../hooks/useLocalizedPath';

/* ── Tool categories for filter ── */
const CATEGORIES = [
  { value: 'all', label: 'Todas' },
  { value: 'vivienda', label: 'Vivienda' },
  { value: 'impuestos', label: 'Impuestos' },
  { value: 'credito', label: 'Crédito' },
  { value: 'ahorro', label: 'Ahorro' },
  { value: 'inversion', label: 'Inversión' },
  { value: 'newcomer', label: 'Recién llegados' },
];

/* ── Active tools ── */
const tools = [
  {
    id: 'mortgage-calc',
    routeKey: 'mortgageCalc',
    imageSrc: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=600&q=80',
    label: 'Calculadora de hipotecas',
    description: 'Simula pagos mensuales, calcula seguro CMHC y compara escenarios de tasa fija vs variable.',
    badge: 'Gratis',
    badgeColor: 'success',
    time: '2 min',
    features: ['Simulación de pago mensual', 'Cálculo CMHC automático'],
    preview: 'Pago estimado: $1,842 / mes',
    cta: 'Calcular ahora',
    categories: ['vivienda', 'newcomer'],
    active: true,
  },
  {
    id: 'budget-planner',
    routeKey: 'budgetPlanner',
    imageSrc: 'https://images.unsplash.com/photo-1554224154-26032ffc0d07?w=600&q=80',
    label: 'Planificador de presupuesto',
    description: 'Controla ingresos y gastos, visualiza con gráficos y recibe insights personalizados.',
    badge: 'Popular',
    badgeColor: 'primary',
    time: '5 min',
    features: ['Control de gastos e ingresos', 'Visualización con gráficos'],
    preview: 'Ahorro mensual estimado: $680',
    cta: 'Abrir herramienta',
    categories: ['ahorro', 'newcomer'],
    active: true,
  },
  {
    id: 'cra-refund',
    routeKey: 'tools',
    imageSrc: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=600&q=80',
    label: 'Calculadora de devolución de impuestos',
    description: 'Estima cuánto podrías recibir este año del CRA según tu ingreso y deducciones.',
    badge: 'Popular',
    badgeColor: 'primary',
    time: '3 min',
    features: ['Estimación de reembolso CRA', 'Deducciones para newcomers'],
    preview: 'Reembolso estimado: $1,230',
    cta: 'Disponible pronto',
    categories: ['impuestos', 'newcomer'],
    active: false,
  },
  {
    id: 'rrsp-tfsa',
    routeKey: 'rrspCalc',
    imageSrc: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=600&q=80',
    label: 'RRSP vs TFSA vs FHSA',
    description: 'Te recomendamos el orden óptimo para contribuir según tu situación real como newcomer.',
    badge: 'Nuevo',
    badgeColor: 'new',
    time: '3 min',
    features: ['Recomendación personalizada', 'Proyección a 10 años'],
    preview: 'Ahorro fiscal estimado: $2,450',
    cta: 'Calcular ahora',
    categories: ['inversion', 'ahorro', 'newcomer'],
    active: true,
  },
  {
    id: 'credit-score-sim',
    routeKey: 'tools',
    imageSrc: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=600&q=80',
    label: 'Simulador de credit score',
    description: 'Descubre cómo mejorar tu score canadiense con acciones concretas.',
    badge: 'Nueva herramienta',
    badgeColor: 'new',
    time: '2 min',
    features: ['Simulación de factores de score', 'Consejos personalizados'],
    preview: 'Score proyectado: 720',
    cta: 'Disponible pronto',
    categories: ['credito', 'newcomer'],
    active: false,
  },
];

/* ── Badge color map ── */
const badgeStyles = {
  success: { bgcolor: '#E8F5E9', color: '#2E7D32' },
  primary: { bgcolor: '#EEF2F8', color: '#1E3A5F' },
  new: { bgcolor: '#FFF8E1', color: '#7D5A00' },
};

/* ── Authority reasons (Section 6) ── */
const authorityReasons = [
  {
    icon: UpdateIcon,
    title: 'Datos actualizados regularmente',
    desc: 'Cada herramienta se revisa y actualiza para reflejar condiciones vigentes en Canadá.',
  },
  {
    icon: GroupsIcon,
    title: 'Enfocadas en newcomers',
    desc: 'Diseñadas para resolver las dudas financieras más comunes de recién llegados latinos.',
  },
  {
    icon: FreeIcon,
    title: 'Sin costo para usuarios',
    desc: 'Todas las herramientas son gratuitas. Sin registro, sin tarjeta de crédito.',
  },
];

/* ── Internal links (Section 7) ── */
const internalLinks = [
  {
    icon: CreditCardIcon,
    label: 'Comparar tarjetas de crédito',
    desc: '110+ tarjetas analizadas con FinMaple Score',
    routeKey: 'creditCards',
  },
  {
    icon: BankIcon,
    label: 'Ver mejores cuentas bancarias',
    desc: 'Cuentas sin fees y con bonos para newcomers',
    routeKey: 'accountComparator',
  },
  {
    icon: TrendingUpIcon,
    label: 'Explorar tasas hipotecarias',
    desc: 'Tasas fijas y variables de los 6 principales bancos',
    routeKey: 'mortgages',
  },
];

/* ── Fade-in on scroll hook (DOM-only, hydration-safe) ── */
const useFadeIn = () => {
  const ref = useRef(null);
  useEffect(() => {
    if (navigator.webdriver) return; // skip during Puppeteer prerender
    const el = ref.current;
    if (!el) return;
    if (el.getBoundingClientRect().top < window.innerHeight) return;
    el.style.opacity = '0';
    el.style.transform = 'translateY(20px)';
    el.style.transition = 'opacity 0.6s ease-out, transform 0.6s ease-out';
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0)';
          obs.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
};

/* ── Tool card component ── */

const ToolCard = ({ tool, lp }) => {
  const style = badgeStyles[tool.badgeColor] || badgeStyles.success;
  const isComingSoon = !tool.active;

  return (
    <Box
      sx={{
        bgcolor: '#FFFFFF',
        border: '1px solid #E6E9EE',
        borderRadius: '14px',
        boxShadow: '0 1px 8px rgba(11,18,32,0.06)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        opacity: isComingSoon ? 0.85 : 1,
        transition: 'transform 0.18s ease, box-shadow 0.18s ease',
        '&:hover': {
          transform: isComingSoon ? 'none' : 'translateY(-4px)',
          boxShadow: isComingSoon
            ? '0 1px 8px rgba(11,18,32,0.06)'
            : '0 14px 40px rgba(0,0,0,0.08)',
        },
      }}
    >
      {/* Image + badges */}
      <Box sx={{ position: 'relative' }}>
        <Box
          component="img"
          src={tool.imageSrc}
          alt={tool.label}
          loading="lazy"
          sx={{ width: '100%', height: 180, objectFit: 'cover', display: 'block' }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            left: 12,
            display: 'flex',
            gap: 0.75,
          }}
        >
          <Chip
            label={tool.badge}
            size="small"
            sx={{
              height: '24px',
              fontSize: '12px',
              fontWeight: 600,
              ...style,
              border: 'none',
            }}
          />
          <Chip
            icon={<TimerIcon sx={{ fontSize: '12px !important' }} />}
            label={tool.time}
            size="small"
            sx={{
              height: '24px',
              fontSize: '12px',
              fontWeight: 500,
              bgcolor: 'rgba(0,0,0,0.5)',
              color: '#fff',
              border: 'none',
              backdropFilter: 'blur(4px)',
              '& .MuiChip-icon': { color: '#fff' },
            }}
          />
        </Box>

        {/* Coming soon overlay */}
        {isComingSoon && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(11,18,32,0.35)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Chip
              label="En desarrollo"
              sx={{
                height: 28,
                fontSize: '12px',
                fontWeight: 600,
                bgcolor: '#FFFFFF',
                color: '#1A1A1A',
              }}
            />
          </Box>
        )}
      </Box>

      {/* Content */}
      <Box sx={{ p: { xs: 3, md: 4 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography sx={{ fontSize: '17px', fontWeight: 600, color: '#1A1A1A', mb: 0.75 }}>
          {tool.label}
        </Typography>

        <Typography sx={{ fontSize: '13px', color: '#1A1A1A', lineHeight: 1.6, mb: 2 }}>
          {tool.description}
        </Typography>

        {/* Mini preview */}
        <Box
          sx={{
            bgcolor: '#F7F9FC',
            borderRadius: '8px',
            px: 2,
            py: 1.5,
            mb: 2,
            borderLeft: '3px solid #1E3A5F',
          }}
        >
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 600,
              color: '#1E3A5F',
              fontFeatureSettings: "'tnum'",
            }}
          >
            {tool.preview}
          </Typography>
          <Typography sx={{ fontSize: '12px', color: '#5F6B7A', mt: 0.25 }}>
            Resultado de ejemplo
          </Typography>
        </Box>

        {/* Feature checklist */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 3, flex: 1 }}>
          {tool.features.map((f) => (
            <Box key={f} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon sx={{ fontSize: 14, color: '#2E7D32' }} />
              <Typography sx={{ fontSize: '13px', color: '#1A1A1A', lineHeight: 1.4 }}>
                {f}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* CTA */}
        {tool.active ? (
          <Button
            variant="contained"
            component={RouterLink}
            to={lp(tool.routeKey)}
            fullWidth
            endIcon={<ArrowForwardIcon sx={{ fontSize: '16px' }} />}
            sx={{
              bgcolor: '#1E3A5F',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.9rem',
              py: 1.3,
              borderRadius: '10px',
              transition: 'transform 0.18s ease, box-shadow 0.18s ease',
              '&:hover': {
                bgcolor: '#1A3152',
                transform: 'translateY(-1px)',
                boxShadow: '0 6px 16px rgba(30,58,95,0.20)',
              },
            }}
          >
            {tool.cta}
          </Button>
        ) : (
          <Button
            variant="outlined"
            disabled
            fullWidth
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.9rem',
              py: 1.3,
              borderRadius: '10px',
              borderColor: '#E6E9EE',
              color: '#5F6B7A',
            }}
          >
            {tool.cta}
          </Button>
        )}
      </Box>
    </Box>
  );
};

/* ── Schema ── */

const buildToolsSchema = (lng) => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Herramientas Financieras Gratuitas - FinMaple',
  description: 'Calculadora de hipotecas y planificador de presupuesto. Herramientas gratuitas para manejar tus finanzas en Canadá.',
  url: 'https://www.finmaple.com/herramientas',
  publisher: {
    '@type': 'Organization',
    name: 'FinMaple',
    url: 'https://www.finmaple.com',
  },
  inLanguage: lng,
  availableLanguage: ['es', 'en', 'fr'],
});

/* ── Page component ── */

const FinancialToolsPage = () => {
  const { i18n } = useTranslation();
  const lp = useLocalizedPath();
  const [filter, setFilter] = useState('all');

  const filteredTools = filter === 'all'
    ? tools
    : tools.filter((t) => t.categories.includes(filter));

  const gridRef = useFadeIn();
  const authorityRef = useFadeIn();
  const linksRef = useFadeIn();

  return (
    <Box sx={{ bgcolor: '#FFFFFF', minHeight: '100vh' }}>
      <SEOHead
        seoKey="tools"
        title="Herramientas Financieras"
        description="Calculadora de hipotecas y planificador de presupuesto. Herramientas gratuitas para manejar tus finanzas en Canadá."
        canonical="/herramientas"
        schema={buildToolsSchema(i18n.language)}
        routeKey="tools"
        noIndex={i18n.language !== 'es'}
      />

      {/* ── 1. HERO ── */}
      <PageHeader
        variant="functional"
        title="Herramientas financieras"
        subtitle="Calculadoras prácticas para ahorrar dinero real en Canadá."
        breadcrumbs={[{ label: 'Herramientas' }]}
        maxWidth="lg"
      >
        {/* Hero badges */}
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 3 }}>
          {[
            { icon: '⚡', text: 'Sin registro' },
            { icon: '📊', text: 'Resultados instantáneos' },
            { icon: '🇪🇸', text: 'En español claro' },
          ].map((b) => (
            <Box
              key={b.text}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                bgcolor: '#FFFFFF',
                border: '1px solid #E6E9EE',
                borderRadius: '8px',
                px: 1.5,
                py: 0.75,
              }}
            >
              <Typography sx={{ fontSize: '14px' }}>{b.icon}</Typography>
              <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#1A1A1A' }}>
                {b.text}
              </Typography>
            </Box>
          ))}
        </Box>
      </PageHeader>

      {/* ── 2. CATEGORY FILTER ── */}
      <Box component="section" sx={{ bgcolor: '#FFFFFF', pt: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1A1A1A', mb: 1.5 }}>
            Filtra por categoría
          </Typography>
          <ToggleButtonGroup
            value={filter}
            exclusive
            onChange={(_, v) => v && setFilter(v)}
            sx={{
              flexWrap: 'wrap',
              gap: 0.75,
              '& .MuiToggleButtonGroup-grouped': {
                border: '1px solid #E6E9EE !important',
                borderRadius: '8px !important',
                ml: '0 !important',
              },
            }}
            aria-label="Filtrar herramientas por categoría"
          >
            {CATEGORIES.map((c) => (
              <ToggleButton
                key={c.value}
                value={c.value}
                sx={{
                  textTransform: 'none',
                  fontSize: '12px',
                  fontWeight: 500,
                  px: 2,
                  py: 0.75,
                  color: '#5F6B7A',
                  transition: 'all 0.15s ease',
                  '&.Mui-selected': {
                    bgcolor: '#1E3A5F',
                    color: '#fff',
                    borderColor: '#1E3A5F !important',
                    '&:hover': { bgcolor: '#1A3152' },
                  },
                  '&:hover': { bgcolor: '#F7F9FC' },
                }}
              >
                {c.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Container>
      </Box>

      {/* ── 3. TOOL GRID ── */}
      <Box ref={gridRef} component="section" sx={{ bgcolor: '#FFFFFF', pt: { xs: 4, md: 5 }, pb: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            {filteredTools.map((tool) => (
              <Grid item xs={12} sm={6} md={4} key={tool.id}>
                <ToolCard tool={tool} lp={lp} />
              </Grid>
            ))}
          </Grid>

          {filteredTools.length === 0 && (
            <Typography sx={{ textAlign: 'center', color: '#5F6B7A', py: 6 }}>
              No hay herramientas en esta categoría aún. Estamos trabajando en ello.
            </Typography>
          )}
        </Container>
      </Box>

      {/* ── 6. AUTHORITY SECTION ── */}
      <Box
        ref={authorityRef}
        component="section"
        sx={{ bgcolor: '#F7F9FC', py: { xs: 6, md: 8 } }}
      >
        <Container maxWidth="lg">
            <Typography
              variant="h4"
              component="h2"
              sx={{ fontWeight: 600, color: '#1A1A1A', mb: 1, textAlign: 'center' }}
            >
              ¿Por qué usar las herramientas de FinMaple?
            </Typography>
            <Typography
              sx={{
                color: '#1A1A1A',
                fontSize: '15px',
                lineHeight: 1.6,
                mb: 5,
                textAlign: 'center',
                maxWidth: '55ch',
                mx: 'auto',
              }}
            >
              Diseñadas para resolver las preguntas financieras más comunes de los latinos en Canadá.
            </Typography>

            <Grid container spacing={4} justifyContent="center">
              {authorityReasons.map((r, i) => {
                const Icon = r.icon;
                return (
                  <Grid item xs={12} sm={4} key={i}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: '14px',
                          bgcolor: '#F7F9FC',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mx: 'auto',
                          mb: 2,
                        }}
                      >
                        <Icon sx={{ fontSize: 28, color: '#1E3A5F' }} />
                      </Box>
                      <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#1A1A1A', mb: 0.75 }}>
                        {r.title}
                      </Typography>
                      <Typography sx={{ fontSize: '13px', color: '#1A1A1A', lineHeight: 1.6, maxWidth: '30ch', mx: 'auto' }}>
                        {r.desc}
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Container>
      </Box>

      {/* ── 7. INTERNAL LINKING ── */}
      <Box
        ref={linksRef}
        component="section"
        sx={{ bgcolor: '#FFFFFF', py: { xs: 6, md: 8 } }}
      >
        <Container maxWidth="lg">
            <Typography
              variant="h4"
              component="h2"
              sx={{ fontWeight: 600, color: '#1A1A1A', mb: 1, textAlign: 'center' }}
            >
              Después de usar una herramienta, te recomendamos:
            </Typography>
            <Typography
              sx={{
                color: '#1A1A1A',
                fontSize: '15px',
                lineHeight: 1.6,
                mb: 5,
                textAlign: 'center',
                maxWidth: '55ch',
                mx: 'auto',
              }}
            >
              Aplica lo que aprendiste con nuestros comparadores.
            </Typography>

            <Grid container spacing={3} justifyContent="center">
              {internalLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <Grid item xs={12} sm={4} key={link.routeKey}>
                    <Box
                      component={RouterLink}
                      to={lp(link.routeKey)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        textDecoration: 'none',
                        bgcolor: '#F7F9FC',
                        border: '1px solid #E6E9EE',
                        borderRadius: '12px',
                        p: 2.5,
                        transition: 'all 0.18s ease',
                        '&:hover': {
                          bgcolor: '#F7F9FC',
                          borderColor: '#1E3A5F',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
                        },
                        '&:focus-visible': {
                          outline: '2px solid #1E3A5F',
                          outlineOffset: 2,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: '10px',
                          bgcolor: '#FFFFFF',
                          border: '1px solid #E6E9EE',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Icon sx={{ fontSize: 22, color: '#1E3A5F' }} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1A1A1A', mb: 0.25 }}>
                          {link.label}
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: '#5F6B7A' }}>
                          {link.desc}
                        </Typography>
                      </Box>
                      <ArrowForwardIcon sx={{ fontSize: 18, color: '#5F6B7A', flexShrink: 0 }} />
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Container>
      </Box>

      <TrustStrip />
      <NewsletterSection />
    </Box>
  );
};

export default FinancialToolsPage;
